import dayjs from "dayjs";

export interface PlannerAssignment {
  slot_id: string;
  appointment_id: string;
  modified_on: string;
  can_move: boolean;
  can_clear: boolean;
}
export interface PlannerEmployee {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  worker_id?: string;
  departments?: { id: string; name: string }[];
  is_analog: boolean;
  assignment: PlannerAssignment | null;
}
export interface PlannerSlot {
  id: string;
  start_date: string;
  end_date: string;
  employee_id: string | null;
  occupied: boolean;
  usable: boolean;
  unavailable_reason?: "past" | "disabled" | "deleted" | null;
}
export interface AssignmentSnapshot {
  id: string;
  title: string;
  employees: PlannerEmployee[];
  slots: PlannerSlot[];
}
export type AssignmentPlan = Record<string, string | null>;
export const initialPlan = (snapshot: AssignmentSnapshot): AssignmentPlan =>
  Object.fromEntries(
    snapshot.employees.map((employee) => [
      employee.id,
      employee.assignment?.slot_id ?? null,
    ]),
  );
export const employeeInSlot = (plan: AssignmentPlan, slotId: string) =>
  Object.keys(plan).find((id) => plan[id] === slotId);
export const canClearEmployee = (employee: PlannerEmployee) =>
  !employee.assignment || employee.assignment.can_clear;
export const canMoveEmployee = (employee: PlannerEmployee) =>
  !employee.assignment || employee.assignment.can_move;
export const slotDay = (slot: PlannerSlot) =>
  dayjs(slot.start_date).format("YYYY-MM-DD");
export const PLANNER_RANGE_DASH = "\u2013";
export const slotTime = (slot: PlannerSlot) =>
  `${dayjs(slot.start_date).format("HH:mm")} ${PLANNER_RANGE_DASH} ${dayjs(slot.end_date).format("HH:mm")}`;
export const slotDescription = (slot?: PlannerSlot) =>
  slot
    ? `${dayjs(slot.start_date).format("DD.MM.YYYY")}, ${slotTime(slot)}`
    : PLANNER_RANGE_DASH;
export function slotUnavailableReason(slot: PlannerSlot) {
  if (slot.unavailable_reason) return slot.unavailable_reason;
  if (slot.usable !== true) return "unknown";
  if (slot.occupied && !slot.employee_id) return "reserved";
  return null;
}
export function assignEmployee(
  snapshot: AssignmentSnapshot,
  plan: AssignmentPlan,
  employeeId: string,
  slotId: string,
  replace = false,
): AssignmentPlan {
  const employee = snapshot.employees.find((entry) => entry.id === employeeId);
  const slot = snapshot.slots.find((entry) => entry.id === slotId);
  if (
    !employee ||
    !slot ||
    !canMoveEmployee(employee) ||
    slotUnavailableReason(slot)
  )
    return plan;
  const occupantId = employeeInSlot(plan, slotId);
  if (occupantId === employeeId) return plan;
  const occupant = snapshot.employees.find((entry) => entry.id === occupantId);
  if (occupantId && (!replace || !occupant || !canClearEmployee(occupant)))
    return plan;
  return {
    ...plan,
    ...(occupantId ? { [occupantId]: null } : {}),
    [employeeId]: slotId,
  };
}
export function planChanges(
  snapshot: AssignmentSnapshot,
  plan: AssignmentPlan,
) {
  return snapshot.employees
    .filter(
      (employee) =>
        (employee.assignment?.slot_id ?? null) !== plan[employee.id],
    )
    .map((employee) => ({
      subject_id: employee.id,
      slot_id: plan[employee.id],
      previous_slot_id: employee.assignment?.slot_id ?? null,
      appointment_id: employee.assignment?.appointment_id ?? null,
      modified_on: employee.assignment?.modified_on ?? null,
    }));
}
